import React, { useCallback, useEffect, useState } from 'react';
import { Ekran, POMOC } from '../pomoc/tekstovi';

/**
 * Pomoć za jedan ekran: čemu služi, šta se radi, šta znače oznake, gde se greši.
 *
 * ZAŠTO JE SKLOPLJIVA, A OTVORENA PRVI PUT
 *   Čovek koji prvi put otvori ekran ne zna ni da pomoć postoji — zato se prvi
 *   put otvori sama. Ko je pročitao, sklopi je i ostaje sklopljena; ko je
 *   sklopio pa zatreba, otvori je jednim klikom. Zapamćeno je PO EKRANU, jer
 *   čovek nauči planiranje pa mu analitika i dalje treba.
 *
 * ZAŠTO NE TOOLTIP
 *   Ono što ovde stoji je redosled posla i cena preskočenog koraka. To se ne
 *   čita iz oblačića koji nestaje kad se miš pomeri, nego iz teksta koji može
 *   da stoji otvoren dok se radi.
 *
 * ZAŠTO STOJI NA VRHU EKRANA
 *   Ispod radnih lista bi je našao samo onaj ko je i bez nje znao šta radi.
 */
interface Props {
  ekran: Ekran;
}

/** Pamćenje po ekranu. Isti oblik ključa kao kod sklopivih odeljaka. */
function kljucFor(ekran: Ekran): string {
  return `elbs.pomoc.${ekran}`;
}

export default function Pomoc({ ekran }: Props) {
  const tekst = POMOC[ekran];
  const kljuc = kljucFor(ekran);

  const [otvorena, setOtvorena] = useState<boolean>(() => {
    // Pregledač ume da zabrani lokalno čuvanje (privatni prozor, obrisani
    // podaci). Tada se ne pamti ništa, a pomoć se ponaša kao prvi put — što je
    // tačnije od toga da je nema.
    try {
      const sirovo = localStorage.getItem(kljuc);
      return sirovo === null ? true : sirovo === '1';
    } catch {
      return true;
    }
  });

  // Ista komponenta služi sve tabove: pri prelasku na drugi ekran stanje se
  // čita ponovo za NJEGA. Do 15.10.2026. je ostajalo stanje prethodnog ekrana,
  // pa je pomoć zatvorena na jednom bila zatvorena i na drugom (analiza 2.0, B7).
  useEffect(() => {
    try {
      const sirovo = localStorage.getItem(kljuc);
      setOtvorena(sirovo === null ? true : sirovo === '1');
    } catch {
      setOtvorena(true);
    }
  }, [kljuc]);

  const prebaci = useCallback(() => {
    setOtvorena((bilo) => {
      const sad = !bilo;
      try {
        localStorage.setItem(kljuc, sad ? '1' : '0');
      } catch {
        // Nezapamćen izbor nije greška koju čovek treba da vidi — pomoć samo
        // ne pamti stanje do sledećeg osvežavanja.
      }
      return sad;
    });
  }, [kljuc]);

  return (
    <section className="pomoc" id={`pomoc-${ekran}`}>
      <h2 className="pomoc__naslov">
        <button
          type="button"
          className="disc__head"
          aria-expanded={otvorena}
          aria-controls={`pomoc-${ekran}-telo`}
          onClick={prebaci}
        >
          <span className="disc__caret" aria-hidden="true">›</span>
          <span className="disc__title">
            Pomoć
            <span className="muted"> · {tekst.naslov}</span>
          </span>
          {!otvorena && <span className="muted pomoc__poziv">kako se ovo radi</span>}
        </button>
      </h2>

      {otvorena && (
        <div className="pomoc__telo" id={`pomoc-${ekran}-telo`}>
          <p className="pomoc__sta">{tekst.cemuSluzi}</p>

          <h3 className="pomoc__podnaslov">Šta se ovde radi</h3>
          <ol className="pomoc__koraci">
            {tekst.koraci.map((korak) => <li key={korak}>{korak}</li>)}
          </ol>

          {tekst.oznake && tekst.oznake.length > 0 && (
            <>
              <h3 className="pomoc__podnaslov">Šta znači ono što vidiš</h3>
              <dl className="pomoc__oznake">
                {tekst.oznake.map((o) => (
                  <React.Fragment key={o.znak}>
                    <dt>{o.znak}</dt>
                    <dd>{o.znaci}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </>
          )}

          {tekst.zamke && tekst.zamke.length > 0 && (
            <>
              <h3 className="pomoc__podnaslov pomoc__podnaslov--pazi">Gde se greši</h3>
              <ul className="pomoc__zamke">
                {tekst.zamke.map((z) => <li key={z}>{z}</li>)}
              </ul>
            </>
          )}
        </div>
      )}
    </section>
  );
}
